import {useMemo} from 'react';
import {
  useErpSyncedTypes,
  type ErpSyncedType,
} from '@/matic/features/erp/record-provenance';
import {useObjectType} from '@/matic/features/master-data/hooks/useObjectType';
import {PART_ENTITY_KIND} from '../types';

/**
 * Resolve which ObjectType plays `ErpEntityKind.PART` for this tenant.
 *
 * This is the whole reason the tracker is a reusable surface rather than a
 * TechComm page: `erp_sync_config` binds a code-owned *kind* to whatever type
 * and fields the tenant happens to have, so the page asks "which type is PART
 * for me?" instead of hardcoding a UUID. A different Cetec tenant pointing the
 * sync at their own "Component" type gets the same page with no code change.
 *
 * `useErpSyncedTypes` returns a Map keyed by ObjectType id, so this is the
 * inverse lookup — there is no kind-keyed accessor today.
 */
export function usePartObjectType() {
  const {data: syncedTypes, isLoading: typesLoading} = useErpSyncedTypes();

  const syncedPart: ErpSyncedType | undefined = useMemo(() => {
    if (!syncedTypes) return undefined;
    for (const entry of syncedTypes.values()) {
      if (entry.entityKind === PART_ENTITY_KIND) return entry;
    }

    return undefined;
  }, [syncedTypes]);

  // The synced-types response is deliberately narrow (no fields), so the
  // hydrated type is a second fetch — same one Master Data and the Library
  // bucket make, so it dedupes on the shared cache key.
  const {data: objectType, isLoading: typeLoading} = useObjectType(
    syncedPart?.targetObjectTypeId ?? ''
  );

  return {
    syncedPart,
    objectType,
    isLoading: typesLoading || (Boolean(syncedPart) && typeLoading),
    /** No PART sync configured — the page cannot render and should not be linked to. */
    isUnavailable: !typesLoading && !syncedPart,
  };
}
